import axios from "axios";
import { toast } from "sonner";
import { globalErrorHandler, ErrorUtils, ErrorSeverity } from "../lib/errorHandler";
import { mapAttendanceToPointage, mapUiStatusToBackend, mapUiTypeToBackend } from "../utils/attendanceMapper";
import i18n from "../i18n";

// Determine API URL based on environment
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const parts = hostname.split('.');
    const reserved = new Set(['www', 'admin', 'api', 'mawered', 'traefik', 'localhost']);

    // On a tenant subdomain (e.g. acme.mawered.local) — route via subdomain so
    // TenantDatabaseMiddleware can extract the tenant from the Host header.
    // Traefik rule: HostRegexp(*.mawered.local) && PathPrefix(/api) → strip /api → backend
    if (parts.length >= 3 && !reserved.has(parts[0])) {
      return `${window.location.protocol}//${hostname}/api`;
    }

    // Explicit env override for root domain (mawered.local / api.mawered.local)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }

    // Native dev: frontend on :3000/:3001, backend on :8000/:8001
    if (port === '3000') {
      return `${window.location.protocol}//${hostname}:8000`;
    } else if (port === '3001') {
      return `${window.location.protocol}//${hostname}:8001`;
    }
  }

  // Production (nginx strips /api prefix) or SSR
  return '/api/';
};

export const API_BASE_URL = getApiBaseUrl();


// Create axios instance with dynamic base URL
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Log API base URL for debugging

// Export direct axios instance
export const http = axiosInstance;

// Attach interceptors to the underlying axios instance
axiosInstance.interceptors.request.use(
  (config) => {
    // Add auth token if available (check both localStorage and sessionStorage)
    const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };

    // Deduplicate identical concurrent GET requests
    if (config.method === "get" && !config._dedupeSkip) {
      const key = config.url + JSON.stringify(config.params || {});
      if (_inflightRequests.has(key)) {
        config.adapter = () => _inflightRequests.get(key);
      } else {
        const promise = new Promise((resolve, reject) => {
          config._dedupeResolve = resolve;
          config._dedupeReject = reject;
          config._dedupeKey = key;
        });
        _inflightRequests.set(key, promise);
      }
    }

    return config;
  },
  (error) => {
    const enhancedError = globalErrorHandler.handleError(error, {
      phase: "request_setup",
    });
    return Promise.reject(enhancedError);
  }
);

// In-flight GET deduplication: key → Promise
const _inflightRequests = new Map();

// Token refresh state — shared across all concurrent in-flight requests
let isRefreshing = false;
let _refreshSubscribers = [];
let _logoutInProgress = false;

function _doLogout() {
  if (_logoutInProgress) return;
  _logoutInProgress = true;
  localStorage.removeItem("access_token");
  localStorage.removeItem("user");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("user_data");
  setTimeout(() => {
    window.location.href = "/login";
  }, 500);
}

// Enhanced response interceptor with comprehensive error handling
axiosInstance.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;

    // Log slow requests in development
    if (process.env.NODE_ENV === "development" && duration > 3000) {
      console.warn(`⚠️ Slow request detected: ${response.config.url} took ${duration}ms`);
    }

    // Resolve dedup promise and remove from map
    if (response.config._dedupeKey) {
      _inflightRequests.delete(response.config._dedupeKey);
      if (response.config._dedupeResolve) response.config._dedupeResolve(response);
    }

    return response;
  },
  async (error) => {
    // Clean up dedup map on error
    const key = error.config?._dedupeKey;
    if (key) {
      _inflightRequests.delete(key);
      if (error.config._dedupeReject) error.config._dedupeReject(error);
    }

    // 401 → attempt transparent token refresh before surfacing the error
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // Don't recurse: skip refresh if this IS the refresh call or already retried
      if (originalRequest._retried || originalRequest.url?.includes("/auth/refresh")) {
        _doLogout();
        return Promise.reject(globalErrorHandler.handleError(error, { phase: "response" }));
      }

      // If a refresh is already in flight, queue this request behind it
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshSubscribers.push((newToken) => {
            if (!newToken) { reject(error); return; }
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;
      originalRequest._retried = true;
      const currentToken =
        localStorage.getItem("access_token") || sessionStorage.getItem("access_token");

      try {
        const { data } = await axiosInstance.post("v1/auth/refresh", null, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        const newToken = data.access_token;
        // Write to whichever storage held the old token
        if (localStorage.getItem("access_token")) {
          localStorage.setItem("access_token", newToken);
        } else {
          sessionStorage.setItem("access_token", newToken);
        }
        _refreshSubscribers.forEach((cb) => cb(newToken));
        _refreshSubscribers = [];
        isRefreshing = false;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch {
        _refreshSubscribers.forEach((cb) => cb(null));
        _refreshSubscribers = [];
        isRefreshing = false;
        _doLogout();
        return Promise.reject(globalErrorHandler.handleError(error, { phase: "response" }));
      }
    }

    // Non-401 errors — standard pipeline
    const enhancedError = globalErrorHandler.handleError(error, {
      duration: error.config?.metadata ? new Date() - error.config.metadata.startTime : null,
      phase: "response",
    });

    enhancedError.backendDetail = error.response?.data?.detail || null;

    const silentRoutes = [
      "/zkteco/",
      "/sites",
      "/organization-chart",
      "/statistics",
      "/dashboard/stats",
      "/dashboard/statistics",
      "/dashboard/attendances",
      "/reports",
      "/pointages",
      "/employees/stats",
      "/companies",
      "/societe",
      "/organigramme",
      "/auth/login",
      "/employees/me",
      "/payroll/my-entries",
      "/payroll/periods",
      "/payroll/settings",
      "/absences/me",
      "/absences/",
      "/leave-policies/balances/me",
      "/wellbeing/",
      "/skills/me",
      "/career/plans/me",
      "/recognition/",
      "/holidays/employee-calendars",
      "/holidays/",
      "/daily-attendance/",
      "/security/summary",
      "/security/audit-logs",
      "/security/events",
      "/security/login-history",
      "/approval-workflows",
      "/documents/",
      "/schedules/",
      "/planning",
      "/validation-workflow",
      "/terminal-sync/",
      "/biometric",
      "/roles",
      "/oracle",
      "/companies/ai-settings",
      "/agents/",
      "/esignature/",
      "/analytics/",
      "/succession/",
      "/positions/",
      "/compensation/",
      "/offboarding/",
      "/one-on-one/",
      "/report-builder/",
      "/workforce-planning/",
      "/compliance/",
      "/dsn/",
      "/integrations/",
      "favicon.ico",
    ];

    const isSilentRoute = silentRoutes.some((route) => error.config?.url?.includes(route));
    const isLoginPage = window.location.pathname === "/login";
    const is503 = error.response?.status === 503;

    if (!isSilentRoute && !isLoginPage && !is503) {
      showErrorToast(enhancedError);
    } else {
      console.warn("🔄 API route optionnelle indisponible:", error.config?.url, enhancedError.message);
    }

    handleErrorActions(enhancedError);
    return Promise.reject(enhancedError);
  }
);

/**
 * Show enhanced error toast with actions
 */
function showErrorToast(error) {
  const toastOptions = {
    id: error.errorId,
    duration: getToastDuration(error.severity),
  };

  // Add action buttons for retryable errors
  if (error.canRetry) {
    toastOptions.action = {
      label: i18n.t("errors.retry"),
      onClick: () => {
        // This would ideally retry the last operation
        window.location.reload();
      },
    };
  }

  // Show different toast types based on severity
  switch (error.severity) {
    case ErrorSeverity.LOW:
      toast.info(error.message, toastOptions);
      break;
    case ErrorSeverity.MEDIUM:
      toast.warning(error.title, {
        ...toastOptions,
        description: error.backendDetail || error.message,
      });
      break;
    case ErrorSeverity.HIGH:
      toast.error(error.title, {
        ...toastOptions,
        description: error.backendDetail || error.message,
      });
      break;
    case ErrorSeverity.CRITICAL:
      toast.error(error.title, {
        ...toastOptions,
        description: error.message,
        duration: Infinity, // Keep critical errors visible
        action: {
          label: "Recharger la page",
          onClick: () => window.location.reload(),
        },
      });
      break;
    default:
      toast.error(error.title, {
        ...toastOptions,
        description: error.message,
      });
  }
}

/**
 * Get toast duration based on error severity
 */
function getToastDuration(severity) {
  switch (severity) {
    case ErrorSeverity.LOW:
      return 3000;
    case ErrorSeverity.MEDIUM:
      return 5000;
    case ErrorSeverity.HIGH:
      return 8000;
    case ErrorSeverity.CRITICAL:
      return Infinity;
    default:
      return 5000;
  }
}

/**
 * Handle specific error actions
 */
function handleErrorActions(error) {
  switch (error.action) {
    case "LOGIN_REQUIRED":
      // Clear auth data and redirect to login
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_data");

      // Delay redirect to allow user to see the error message
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
      break;

    case "RELOAD_PAGE":
      // Show confirmation before reloading
      if (window.confirm("Une erreur s'est produite. Voulez-vous recharger la page ?")) {
        window.location.reload();
      }
      break;

    default:
      // No specific action needed
      break;
  }
}

/**
 * Enhanced API wrapper with retry logic
 */
class EnhancedApiWrapper {
  constructor(axiosInstance) {
    this.api = axiosInstance;
  }

  /**
   * Make API request with automatic retry for retryable errors
   */
  async makeRequest(requestFn, operation = "api_request", maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await requestFn();

        // Clear retry attempts on success
        globalErrorHandler.clearRetryAttempts(operation);

        return result;
      } catch (error) {
        lastError = error;

        // Check if error is retryable and we haven't exceeded max retries
        if (ErrorUtils.isRetryable(error) && attempt < maxRetries) {
          // Record retry attempt
          globalErrorHandler.recordRetryAttempt(error.errorId, operation);

          // Calculate delay with exponential backoff
          const delay = 1000 * Math.pow(2, attempt - 1);

          // Show retry toast
          toast.info(`Tentative ${attempt + 1}/${maxRetries}`, {
            description: `Nouvelle tentative dans ${delay / 1000} secondes...`,
            duration: delay,
          });

          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, delay));

          continue;
        }

        // Error is not retryable or max retries exceeded
        break;
      }
    }

    throw lastError;
  }

  /**
   * GET request with enhanced error handling
   */
  async get(url, config = {}) {
    return this.makeRequest(() => this.api.get(url, config), `GET_${url}`, config.maxRetries || 3);
  }

  /**
   * POST request with enhanced error handling
   */
  async post(url, data = {}, config = {}) {
    return this.makeRequest(
      () => this.api.post(url, data, config),
      `POST_${url}`,
      config.maxRetries || 2 // Fewer retries for POST to avoid duplicate creation
    );
  }

  /**
   * PUT request with enhanced error handling
   */
  async put(url, data = {}, config = {}) {
    return this.makeRequest(() => this.api.put(url, data, config), `PUT_${url}`, config.maxRetries || 2);
  }

  /**
   * DELETE request with enhanced error handling
   */
  async delete(url, config = {}) {
    return this.makeRequest(() => this.api.delete(url, config), `DELETE_${url}`, config.maxRetries || 2);
  }

  /**
   * PATCH request with enhanced error handling
   */
  async patch(url, data = {}, config = {}) {
    return this.makeRequest(() => this.api.patch(url, data, config), `PATCH_${url}`, config.maxRetries || 2);
  }
}

// Create enhanced API instance
const enhancedApi = new EnhancedApiWrapper(axiosInstance);

// Export alias for compatibility with code that imports 'api'
export const api = axiosInstance;

// Export alias for compatibility with code that imports 'apiClient'
export const apiClient = enhancedApi;

// ===== API SOCIÉTÉ & ORGANIGRAMME (CONNECTÉES À LA BD) =====

export const employeesAPI = {
  async getEmployees(filters = {}) {
    try {
      const params = { ...filters };
      if (params.filters && typeof params.filters === "object") {
        params.filters = JSON.stringify(params.filters);
      }
      const response = await enhancedApi.get("v1/employees/", { params });
      // Backend returns {employees: [...], total, page, limit}
      // Return the full response data object (not just the array)
      return response.data || { employees: [], total: 0 };
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
      return { employees: [], total: 0 };
    }
  },

  async createEmployee(employeeData) {
    const response = await enhancedApi.post("v1/employees/", employeeData);
    return response.data;
  },

  async updateEmployee(employeeId, employeeData) {
    const response = await enhancedApi.put(`v1/employees/${employeeId}`, employeeData);
    return response.data;
  },

  async deleteEmployee(employeeId) {
    const response = await enhancedApi.delete(`v1/employees/${employeeId}`);
    return response.data;
  },

  async uploadEmployeePhoto(employeeId, file) {
    const form = new FormData();
    form.append("file", file);
    const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    const headers = {
      "Content-Type": undefined, // Let browser set multipart/form-data with boundary
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return http.post(`v1/employees/${employeeId}/photo`, form, { headers });
  },

  // Import en masse
  async downloadTemplate() {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get("v1/employees/import/template", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `template_import_employes_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Erreur téléchargement template:", error);
      throw error;
    }
  },

  async detectColumns(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.post("v1/employees/import/detect-columns", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Erreur détection colonnes:", error);
      throw error;
    }
  },

  async validateImportFile(file, mapping = null) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (mapping) {
        formData.append("mapping", JSON.stringify(mapping));
      }

      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.post("v1/employees/import/validate", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Erreur validation fichier:", error);
      throw error;
    }
  },

  async importEmployees(file, mapping = null) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (mapping) {
        formData.append("mapping", JSON.stringify(mapping));
      }

      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.post("v1/employees/import/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 120000, // 2 minutes pour l'import massif
      });

      return response.data;
    } catch (error) {
      console.error("Erreur import employés:", error);
      throw error;
    }
  },
};

export const documentsAPI = {
  async listTemplates(params = {}) {
    const response = await enhancedApi.get("v1/documents/templates", { params });
    return response.data;
  },

  async previewDocument(payload) {
    const response = await enhancedApi.post("v1/documents/preview", payload);
    return response.data;
  },

  async generateDocuments(payload) {
    const response = await enhancedApi.post("v1/documents/generate", payload);
    return response.data;
  },

  async listHistory(params = {}) {
    const response = await enhancedApi.get("v1/documents/history", { params });
    return response.data;
  },

  async createTemplate(payload) {
    const response = await enhancedApi.post("v1/documents/templates", payload);
    return response.data;
  },

  async updateTemplate(templateId, payload) {
    const response = await enhancedApi.put(`v1/documents/templates/${templateId}`, payload);
    return response.data;
  },

  async deleteTemplate(templateId) {
    const response = await enhancedApi.delete(`v1/documents/templates/${templateId}`);
    return response.data;
  },
};

export const employeeFiltersAPI = {
  async list() {
    const response = await enhancedApi.get("v1/employees/filters");
    return response.data;
  },

  async create(payload) {
    const response = await enhancedApi.post("v1/employees/filters", payload);
    return response.data;
  },

  async update(filterId, payload) {
    const response = await enhancedApi.put(`v1/employees/filters/${filterId}`, payload);
    return response.data;
  },

  async delete(filterId) {
    const response = await enhancedApi.delete(`v1/employees/filters/${filterId}`);
    return response.data;
  },
};

// Société API with enhanced error handling
export const companyAPI = {
  async getCompanyInfo() {
    try {
      const response = await enhancedApi.get("v1/companies/");
      return response?.data;
    } catch (error) {
      // Error is already handled by interceptor, just re-throw
      throw error;
    }
  },

  async updateCompanyInfo(data) {
    try {
      // ✅ CORRECTION : Utiliser axios direct SANS retry automatique pour éviter les toasts multiples
      const response = await api.put("v1/companies/", data, {
        // Désactiver les retries automatiques pour cette opération
        maxRetries: 0,
        timeout: 15000, // 15 secondes max
      });

      return response;
    } catch (error) {
      console.error("❌ updateCompanyInfo échoué:", {
        status: error.response?.status,
        message: error.message,
        responseData: error.response?.data,
      });
      throw error;
    }
  },

  async updateSystemSettings(data) {
    try {
      const response = await api.put("v1/companies/system-settings", data, {
        maxRetries: 0,
        timeout: 15000
      });
      return response;
    } catch (error) {
      console.error("❌ updateSystemSettings échoué:", error.response?.data || error.message);
      throw error;
    }
  },

  async updateHRSettings(data) {
    try {
      const response = await api.put("v1/companies/hr-settings", data, {
        maxRetries: 0,
        timeout: 15000
      });
      return response;
    } catch (error) {
      console.error("❌ updateHRSettings échoué:", error.response?.data || error.message);
      throw error;
    }
  },

  async uploadLogo(file) {
    try {
      // Validation côté client
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error(i18n.t("apiToasts.fileTooLarge"));
      }

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(i18n.t("apiToasts.fileTypeUnsupported"));
      }

      // Créer FormData pour l'upload
      const formData = new FormData();
      formData.append("file", file);

      // ✅ CORRECTION FINALE : Utiliser l'instance API raw pour éviter les interceptors problématiques
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const headers = {
        "Content-Type": undefined, // Let browser set multipart/form-data with boundary
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await http.post(`v1/companies/upload-logo`, formData, {
        timeout: 30000,
        headers,
      });

      return response;
    } catch (error) {
      console.error("❌ Erreur uploadLogo détaillée:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        },
      });
      throw error;
    }
  },

  async getDevises() {
    try {
      const response = await enhancedApi.get("v1/companies/currencies");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des devises:", error);
      // Fallback local en cas d'erreur
      return {
        TND: "Dinar Tunisien",
        EUR: "Euro",
        USD: "Dollar US",
        MAD: "Dirham Marocain",
        XOF: "Franc CFA",
      };
    }
  },

  async getFormesJuridiques() {
    try {
      const response = await enhancedApi.get("v1/companies/legal-forms");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des formes juridiques:", error);
      // Fallback local en cas d'erreur
      return {
        SARL: "Société à Responsabilité Limitée",
        SA: "Société Anonyme",
        SAS: "Société par Actions Simplifiée",
        EURL: "Entreprise Unipersonnelle à Responsabilité Limitée",
        EI: "Entreprise Individuelle",
        AUTO_ENTREPRENEUR: "Auto-entrepreneur",
        ASSOCIATION: "Association",
        AUTRE: "Autre",
      };
    }
  },

  async getFuseauxHoraires() {
    try {
      const response = await enhancedApi.get("v1/companies/timezones");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des fuseaux horaires:", error);
      // Fallback local en cas d'erreur
      return {
        "Africa/Tunis": "Tunis (GMT+1)",
        "Africa/Casablanca": "Casablanca (GMT+1)",
        "Africa/Bamako": "Bamako (GMT+0)",
        "Europe/Paris": "Paris (GMT+1)",
        UTC: "UTC (GMT+0)",
      };
    }
  },

  async getAISettings() {
    return api.get("v1/companies/ai-settings");
  },

  async updateAISettings(data) {
    return api.put("v1/companies/ai-settings", data);
  },

  async testAISettings() {
    return api.post("v1/companies/ai-settings/test");
  },
};

export const organigrammeAPI = {
  async getOrganizationChart() {
    try {
      const response = await enhancedApi.get("v1/organization-chart/");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'organigramme:", error);
      throw error;
    }
  },

  async getOrganizationChartStats() {
    try {
      const response = await enhancedApi.get("v1/organization-chart/statistics");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques d'organigramme:", error);
      throw error;
    }
  },

  async createDepartement(departementData) {
    try {
      const response = await enhancedApi.post("v1/organization-chart/departments", departementData);

      toast.success(i18n.t("apiToasts.deptCreated"), {
        description: i18n.t("apiToasts.deptCreatedDesc", { name: departementData.nom }),
      });

      return response;
    } catch (error) {
      console.error("Erreur lors de la création du département:", error);
      throw error;
    }
  },

  async updateDepartement(departementId, departementData) {
    try {
      const response = await enhancedApi.put(
        `v1/organization-chart/departments/${departementId}`,
        departementData
      );

      toast.success(i18n.t("apiToasts.deptUpdated"), {
        description: i18n.t("apiToasts.deptUpdatedDesc"),
      });

      return response;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du département:", error);
      throw error;
    }
  },

  async deleteDepartement(departementId) {
    try {
      const response = await enhancedApi.delete(`v1/organization-chart/departments/${departementId}`);

      // Ne pas afficher de toast automatique - laissons le composant gérer les messages

      return response;
    } catch (error) {
      console.error(`❌ API Error: Suppression département ${departementId}:`, {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        message: error.message,
      });
      throw error;
    }
  },

  async getEmployees() {
    try {
      const response = await enhancedApi.get("v1/employees/");
      // Backend returns {employees: [...], total, page, limit}
      // Return the employees array for backward compatibility
      return response.data.employees || [];
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
      throw error;
    }
  },

  // Nouvelle API pour filtrer les employés par département
  async getEmployeesByDepartement(departementId = null) {
    try {
      const url = departementId ? `v1/employees?departement_id=${departementId}` : "v1/employees";
      const response = await enhancedApi.get(url);
      return response;
    } catch (error) {
      console.error("Erreur lors de la récupération des employés par département:", error);
      throw error;
    }
  },

  async reorderDepartements(reorderData) {
    try {
      const response = await enhancedApi.post("v1/organization-chart/departments/reorder", reorderData);

      toast.success(i18n.t("apiToasts.orderUpdated"), {
        description: i18n.t("apiToasts.orderUpdatedDesc"),
      });

      return response;
    } catch (error) {
      console.error("Erreur lors de la réorganisation:", error);
      throw error;
    }
  },
};

// ===== API POINTAGES =====
export const pointagesAPI = {
  async getPointages(filters = {}) {
    try {
      const params = {};
      if (filters.skip != null) params.skip = filters.skip;
      if (filters.limit != null) params.limit = filters.limit;
      const employeeFilter = filters.employeeId ?? filters.employee_id;
      if (employeeFilter && employeeFilter !== "all") params.employee_id = employeeFilter;
      if (filters.attendance_type && filters.attendance_type !== "all") {
        params.attendance_type = mapUiTypeToBackend(filters.attendance_type);
      }
      if (filters.status && filters.status !== "all") {
        params.status = mapUiStatusToBackend(filters.status);
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await enhancedApi.get(`v1/attendances/`, { params });
      const attendances = Array.isArray(response.data) ? response.data : [];
      const pointages = attendances.map(mapAttendanceToPointage).filter(Boolean);

      return {
        ...response,
        data: {
          pointages,
          total: pointages.length,
          raw: attendances,
        },
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des pointages:", error);
      throw error;
    }
  },

  async createPointage(pointageData) {
    try {
      const response = await enhancedApi.post("v1/attendances", pointageData);
      return response;
    } catch (error) {
      console.error("Erreur lors de la création du pointage:", error);
      throw error;
    }
  },

  async validatePointage(pointageId, validationData) {
    try {
      const response = await enhancedApi.put(`v1/attendances/${pointageId}`, validationData);
      return response;
    } catch (error) {
      console.error("Erreur lors de la validation du pointage:", error);
      throw error;
    }
  },

  async getAttendanceLogs(filters = {}) {
    try {
      const params = {};
      if (filters.skip != null) params.skip = filters.skip;
      if (filters.limit != null) params.limit = filters.limit;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.device_sn) params.device_sn = filters.device_sn;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.processed) params.processed = filters.processed;

      const response = await enhancedApi.get(`v1/attendances/logs`, { params });

      const data = response.data;
      const logs = Array.isArray(data.items) ? data.items : [];

      const pointages = logs.map(mapAttendanceToPointage).filter(Boolean);

      return {
        ...response,
        data: {
          pointages,
          total: data.total || pointages.length,
          raw: logs,
        },
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des logs de pointage:", error);
      throw error;
    }
  },
};

// ===== API PLANNING =====
export const planningAPI = {
  async getPlanning(filters = {}) {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await enhancedApi.get(`v1/planning?${params}`);
      return response;
    } catch (error) {
      console.error("Erreur lors de la récupération du planning:", error);
      throw error;
    }
  },

  async createPlanning(planningData) {
    try {
      const response = await enhancedApi.post("v1/planning", planningData);
      return response;
    } catch (error) {
      console.error("Erreur lors de la création du planning:", error);
      throw error;
    }
  },
};

// ===== API RAPPORTS =====
export const reportsAPI = {
  async generateReport(reportData) {
    try {
      const response = await enhancedApi.post("v1/reports/generate", reportData);
      return response;
    } catch (error) {
      console.error("Erreur lors de la génération du rapport:", error);
      throw error;
    }
  },

  async getReportHistory() {
    try {
      const response = await enhancedApi.get("v1/reports/history");
      return response;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique:", error);
      throw error;
    }
  },
};

// =====================================
// ZKTECO API
// =====================================

/**
 * ZKTeco Terminal Integration API
 */
export const zktecoAPI = {
  /**
   * Get punch logs with filtering options
   */
  getPunchLogs: (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.device_sn) queryParams.append("device_sn", params.device_sn);
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);

    return api.get(`v1/zkteco/punches?${queryParams.toString()}`);
  },

  /**
   * Get a specific terminal by ID
   */
  getTerminal: async (terminalId) => {
    try {
      const response = await api.get(`v1/zkteco/terminals/${terminalId}`);
      return response;
    } catch (error) {
      console.warn("🔄 Erreur API détails terminal:", error.message);
      // Don't return fallback data - let the calling function handle the error
      throw error;
    }
  },

  /**
   * Get list of terminals with filtering
   */
  getTerminals: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.site_id) queryParams.append("site_id", params.site_id);
      if (params.communication_mode) queryParams.append("communication_mode", params.communication_mode);
      if (params.status) queryParams.append("status", params.status);
      if (params.search) queryParams.append("search", params.search);
      // Enable real-time status sync by default
      if (params.sync_status !== false) queryParams.append("sync_status", "true");

      const response = await api.get(`v1/zkteco/terminals?${queryParams.toString()}`);

      // If API returns data, use it directly
      if (response.data && response.data.terminals) {
        return response;
      }

      // If no data from API, return empty result
      return {
        data: {
          terminals: [],
          total: 0,
          page: params.page || 1,
          limit: params.limit || 100,
        },
      };
    } catch (error) {
      console.warn("Erreur API terminaux:", error.message);
      // Optionally allow demo data only if explicitly enabled
      if (process.env.REACT_APP_ENABLE_FAKE_TERMINALS === "true") {
        return {
          data: {
            terminals: [
              {
                id: 1,
                name: "Terminal Accueil",
                communication_mode: "pull",
                terminal_type: "biometric",
                ip_address: "192.168.1.100",
                port: 4370,
                serial_number: "ACM123456789",
                site_id: 1,
                site_name: "Siège Social",
                model: "ZKTeco Mini AC Plus",
                status: "offline",
                capabilities: ["fingerprint", "face", "rfid"],
                firmware_version: "6.60",
                max_users: 1000,
                max_logs: 50000,
                is_online: false,
                supports_push_mode: false,
                last_sync_at: "2025-01-20T10:30:00Z",
                created_at: "2025-01-15T09:00:00Z",
              },
              {
                id: 2,
                name: "Terminal Sortie",
                communication_mode: "push",
                terminal_type: "hybrid",
                ip_address: "192.168.1.101",
                port: 4370,
                serial_number: "ACM987654321",
                site_id: 1,
                site_name: "Siège Social",
                model: "ZKTeco ProFace X",
                status: "online",
                capabilities: ["fingerprint", "face", "wifi"],
                firmware_version: "8.5.1",
                max_users: 3000,
                max_logs: 100000,
                is_online: true,
                supports_push_mode: true,
                last_sync_at: "2025-01-21T14:45:00Z",
                created_at: "2025-01-15T09:00:00Z",
              },
            ],
            total: 2,
          },
        };
      }
      // Default: avoid confusing demo rows; return empty dataset and let UI handle it
      return {
        data: {
          terminals: [],
          total: 0,
          page: params.page || 1,
          limit: params.limit || 100,
        },
      };
    }
  },

  /**
   * Create a new terminal
   */
  createTerminal: async (terminalData) => {
    return await api.post("v1/zkteco/terminals", terminalData);
  },

  /**
   * Update a terminal
   */
  updateTerminal: (terminalId, updateData) => {
    return api.put(`v1/zkteco/terminals/${terminalId}`, updateData);
  },

  /**
   * Delete a terminal
   */
  deleteTerminal: (terminalId) => {
    return api.delete(`v1/zkteco/terminals/${terminalId}`);
  },

  /**
   * Synchronize time for a single terminal
   */
  syncTerminalTime: async (terminalId) => {
    try {
      const response = await api.post(`v1/zkteco/terminals/${terminalId}/sync-time`);
      return response;
    } catch (error) {
      console.error("❌ Erreur synchronisation temps terminal:", error.message);
      throw error;
    }
  },

  /**
   * Synchronize time for multiple terminals
   */
  syncMultipleTerminalsTime: async (terminalIds) => {
    try {
      const response = await api.post("v1/zkteco/terminals/sync-time-bulk", terminalIds);
      return response;
    } catch (error) {
      console.error("❌ Erreur synchronisation temps en lot:", error.message);
      throw error;
    }
  },

  /**
   * Test connection to terminal (Pull mode)
   */
  testConnection: async (testData) => {
    return await api.post("v1/zkteco/test-connection", testData);
  },

  /**
   * Test ADMS connection (Push mode)
   */
  testADMSConnection: async (testData) => {
    return await api.post("v1/zkteco/test-adms", testData);
  },

  /**
   * Health check endpoint
   */
  healthCheck: () => {
    return api.get("v1/zkteco/health");
  },

  /**
   * Push punch data to server (for testing push mode)
   */
  pushPunchData: (punchData) => {
    return api.post("v1/zkteco/push", punchData);
  },

  /**
   * Get available sites for terminal creation
   */
  getSites: async () => {
    return await api.get("v1/sites/");
  },

  /**
   * Get terminal types and capabilities
   */
  getTerminalTypes: () => {
    return Promise.resolve({
      data: {
        terminal_types: [
          { value: "biometric", label: "Biométrique", description: "Terminal avec lecteur d'empreintes" },
          { value: "rfid", label: "RFID", description: "Terminal avec lecteur de cartes RFID" },
          { value: "hybrid", label: "Hybride", description: "Terminal biométrique + RFID" },
          { value: "face_recognition", label: "Reconnaissance faciale", description: "Terminal avec caméra faciale" },
          { value: "qr_code", label: "Code QR", description: "Terminal avec lecteur QR code" },
        ],
        capabilities: [
          { value: "fingerprint", label: "Empreinte digitale", icon: "👆" },
          { value: "face", label: "Reconnaissance faciale", icon: "😊" },
          { value: "rfid", label: "Carte RFID", icon: "💳" },
          { value: "password", label: "Code PIN", icon: "🔢" },
          { value: "wifi", label: "Wi-Fi", icon: "📶" },
          { value: "ethernet", label: "Ethernet", icon: "🌐" },
          { value: "usb", label: "USB", icon: "🔌" },
          { value: "bluetooth", label: "Bluetooth", icon: "📘" },
        ],
        communication_modes: [
          { value: "pull", label: "Pull (Local)", description: "Connexion réseau local directe" },
          { value: "push", label: "Push (ADMS)", description: "Mode push via serveur ADMS" },
        ],
      },
    });
  },

  /**
   * Generic POST method
   */
  post: (url, data = {}, config = {}) => {
    return api.post(url, data, config);
  },

  /**
   * Generic PUT method
   */
  put: (url, data = {}, config = {}) => {
    return api.put(url, data, config);
  },

  /**
   * Generic DELETE method
   */
  delete: (url, config = {}) => {
    return api.delete(url, config);
  },

  /**
   * Download attendance from terminal
   */
  downloadAttendance: (terminalId) => {
    return api.post(`v1/zkteco/terminals/${terminalId}/download-attendance`);
  },

  /**
   * Clear attendance from terminal
   */
  clearAttendance: (terminalId) => {
    return api.post(`v1/zkteco/terminals/${terminalId}/clear-attendance`);
  },

  /**
   * Fetch attendance from multiple terminals
   */
  fetchMultipleTerminalsAttendance: async (terminalIds) => {
    try {
      const response = await api.post("v1/zkteco/terminals/fetch-attendance-bulk", terminalIds);
      return response;
    } catch (error) {
      console.error("❌ Erreur récupération pointages en lot:", error.message);
      throw error;
    }
  },
  async getTerminalDiagnostic(terminalId) {
    return api.get(`v1/zkteco/terminals/${terminalId}/diagnose`);
  },
  async health() {
    return api.get("v1/zkteco/health");
  },
  async getPushMetrics(terminalId) {
    return api.get(`v1/zkteco/terminals/${terminalId}/push-metrics`);
  },
  async refreshTerminalMetrics(terminalId) {
    return api.post(`v1/zkteco/terminals/${terminalId}/refresh-metrics`);
  },

  /**
   * Get unconfigured terminals (detected but not in database)
   */
  getUnconfiguredTerminals: async () => {
    try {
      const response = await api.get("v1/zkteco/unconfigured-terminals");
      return response;
    } catch (error) {
      console.warn("🔄 Erreur récupération terminaux non configurés:", error.message);
      // Fallback avec données vides
      return {
        data: {
          success: false,
          message: "Service indisponible",
          unconfigured_terminals: [],
          configured_count: 0,
          total_detected: 0,
        },
      };
    }
  },

  /**
   * Synchronize user data from specific terminal
   */
  syncTerminalUsers: async (serialNumber, syncRequest) => {
    try {
      const response = await api.post(`v1/terminal-sync/terminals/${serialNumber}/sync-users`, syncRequest);
      return response;
    } catch (error) {
      console.error("❌ Erreur synchronisation utilisateurs:", error.message);
      throw error;
    }
  },

  /**
   * Synchronize user data from all active terminals
   */
  syncAllTerminalsUsers: async (syncRequest) => {
    try {
      const response = await api.post("v1/terminal-sync/terminals/sync-all-users", syncRequest);
      return response;
    } catch (error) {
      console.error("❌ Erreur synchronisation utilisateurs en lot:", error.message);
      throw error;
    }
  },

  /**
   * Get unmapped biometric data
   */
  getUnmappedData: async (limit = 100) => {
    try {
      const response = await api.get(`v1/terminal-sync/terminals/unmapped-data?limit=${limit}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur récupération données non mappées:", error.message);
      throw error;
    }
  },

  /**
   * Manually map registration number to employee
   */
  mapUserData: async (mappingRequest) => {
    try {
      const response = await api.post("v1/terminal-sync/terminals/map-data", mappingRequest);
      return response;
    } catch (error) {
      console.error("❌ Erreur mapping manuel:", error.message);
      throw error;
    }
  },

  /**
   * Get synchronization history
   */
  getSyncHistory: async (terminalSerial = null, limit = 50) => {
    try {
      const params = new URLSearchParams();
      if (terminalSerial) params.append("terminal_serial", terminalSerial);
      params.append("limit", limit);

      const response = await api.get(`v1/terminal-sync/terminals/sync-history?${params.toString()}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur récupération historique:", error.message);
      throw error;
    }
  },

  /**
   * Get biometric data for specific terminal
   */
  getTerminalBiometricData: async (serialNumber, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.mappedOnly) params.append("mapped_only", "true");
      if (options.biometricType) params.append("biometric_type", options.biometricType);
      if (options.limit) params.append("limit", options.limit);

      const response = await api.get(`v1/terminals/${serialNumber}/biometric-data?${params.toString()}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur récupération données biométriques:", error.message);
      throw error;
    }
  },

  /**
   * Get photos for specific terminal
   */
  getTerminalPhotos: async (serialNumber, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.mappedOnly) params.append("mapped_only", "true");
      if (options.includePhotoData) params.append("include_photo_data", "true");
      if (options.limit) params.append("limit", options.limit);

      const response = await api.get(`v1/terminals/${serialNumber}/photos?${params.toString()}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur récupération photos:", error.message);
      throw error;
    }
  },

  /**
   * Search employees for manual mapping
   */
  searchEmployees: async (query, limit = 20) => {
    try {
      const params = new URLSearchParams();
      params.append("search", query);
      params.append("limit", limit);

      const response = await api.get(`v1/employees/?${params.toString()}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur recherche employés:", error.message);
      throw error;
    }
  },

  /**
   * Delete biometric data record
   */
  deleteBiometricData: async (serialNumber, dataId) => {
    try {
      const response = await api.delete(`v1/terminals/${serialNumber}/biometric-data/${dataId}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur suppression données biométriques:", error.message);
      throw error;
    }
  },

  /**
   * Delete photo record
   */
  deletePhoto: async (serialNumber, photoId) => {
    try {
      const response = await api.delete(`v1/terminals/${serialNumber}/photos/${photoId}`);
      return response;
    } catch (error) {
      console.error("❌ Erreur suppression photo:", error.message);
      throw error;
    }
  },
};

// =====================================
// PAYROLL API
// =====================================

export const payrollAPI = {
  /**
   * Get payroll periods with pagination and filtering
   */
  async getPayrollPeriods(filters = {}) {
    try {
      const params = { ...filters };
      if (params.filters && typeof params.filters === "object") {
        params.filters = JSON.stringify(params.filters);
      }
      const response = await enhancedApi.get("v1/payroll/periods", { params });
      return response.data || { items: [], total: 0, page: 1, limit: 20, has_more: false };
    } catch (error) {
      console.error("Erreur lors de la récupération des périodes paie:", error);
      return { items: [], total: 0, page: 1, limit: 20, has_more: false };
    }
  },

  /**
   * Create a new payroll period
   */
  async createPayrollPeriod(periodData) {
    try {
      const response = await enhancedApi.post("v1/payroll/periods", periodData, { maxRetries: 0 });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la période paie:", error);
      throw error;
    }
  },

  /**
   * Update payroll period
   */
  async updatePayrollPeriod(periodId, periodData) {
    try {
      const response = await enhancedApi.put(`v1/payroll/periods/${periodId}`, periodData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la période paie:", error);
      throw error;
    }
  },

  /**
   * Delete payroll period
   */
  async deletePayrollPeriod(periodId) {
    try {
      const response = await enhancedApi.delete(`v1/payroll/periods/${periodId}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la suppression de la période paie:", error);
      throw error;
    }
  },

  /**
   * Get payroll period details
   */
  async getPayrollPeriod(periodId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/periods/${periodId}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération de la période paie:", error);
      throw error;
    }
  },

  /**
   * Calculate payroll for period
   */
  async calculatePayrollPeriod(periodId, options = {}) {
    try {
      const response = await enhancedApi.post(`v1/payroll/periods/${periodId}/calculate`, options);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du calcul de la paie:", error);
      throw error;
    }
  },

  async pollPayrollTask(taskId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du suivi de la tâche paie:", error);
      throw error;
    }
  },

  /**
   * Get payroll entries for a period
   */
  async getPayrollEntries(periodId, filters = {}) {
    try {
      const params = { ...filters };
      const response = await enhancedApi.get(`v1/payroll/periods/${periodId}/entries`, { params });
      return response.data || { items: [], total: 0 };
    } catch (error) {
      console.error("Erreur lors de la récupération des bulletins:", error);
      return { items: [], total: 0 };
    }
  },

  /**
   * Get specific payroll entry details
   */
  async getPayrollEntry(entryId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/entries/${entryId}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du bulletin:", error);
      throw error;
    }
  },

  /**
   * Update payroll entry
   */
  async updatePayrollEntry(entryId, entryData) {
    try {
      const response = await enhancedApi.put(`v1/payroll/entries/${entryId}`, entryData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du bulletin:", error);
      throw error;
    }
  },

  /**
   * Submit payroll period for approval
   */
  async submitPayrollPeriod(periodId, comment = "") {
    try {
      const response = await enhancedApi.post(`v1/payroll/periods/${periodId}/submit`, { comment });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la soumission pour approbation:", error);
      throw error;
    }
  },

  /**
   * Approve payroll period
   */
  async approvePayrollPeriod(periodId, comment = "") {
    try {
      const response = await enhancedApi.post(`v1/payroll/periods/${periodId}/approve`, {
        approve: true,
        comment
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'approbation de la paie:", error);
      throw error;
    }
  },

  /**
   * Reject payroll period
   */
  async rejectPayrollPeriod(periodId, comment) {
    try {
      const response = await enhancedApi.post(`v1/payroll/periods/${periodId}/approve`, {
        approve: false,
        comment
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du rejet de la paie:", error);
      throw error;
    }
  },

  /**
   * Close payroll period
   */
  async closePayrollPeriod(periodId) {
    try {
      const response = await enhancedApi.post(`v1/payroll/periods/${periodId}/close`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la clôture de la paie:", error);
      throw error;
    }
  },

  /**
   * Export payroll period as CSV
   */
  async exportPayrollCSV(periodId, format = "csv") {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(`v1/payroll/periods/${periodId}/export`, {
        params: { format },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `paie_${periodId}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error);
      throw error;
    }
  },

  /**
   * Export accounting entries CSV
   */
  async exportAccountingCSV(periodId) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(`v1/payroll/periods/${periodId}/export/accounting`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `comptabilite_paie_${periodId}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Erreur lors de l'export comptabilité:", error);
      throw error;
    }
  },

  /**
   * Export the WPS Salary Information File (SIF) for a Gulf period.
   */
  async exportWpsSif(periodId) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(`v1/payroll/periods/${periodId}/export/wps`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `wps_${periodId}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Erreur lors de l'export WPS:", error);
      throw error;
    }
  },

  /**
   * Get payroll summary for period
   */
  async getPayrollSummary(periodId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/periods/${periodId}/summary`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du résumé paie:", error);
      throw error;
    }
  },

  /**
   * Get payroll settings
   */
  async getPayrollSettings() {
    try {
      const response = await enhancedApi.get("v1/payroll/settings");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres paie:", error);
      throw error;
    }
  },

  /**
   * Get statutory (legal) payroll rates for a country — read-only, sourced
   * from the backend calculator config.
   */
  async getStatutoryRates(countryCode) {
    try {
      const response = await enhancedApi.get(`v1/payroll/statutory-rates/${countryCode}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des barèmes statutaires:", error);
      throw error;
    }
  },

  /**
   * Get the end-of-service gratuity (EOSB) employer-cost report for a period.
   */
  async getEosbReport(periodId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/periods/${periodId}/eosb-report`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du rapport fin de service:", error);
      throw error;
    }
  },

  /**
   * Update payroll settings
   */
  async updatePayrollSettings(settingsData) {
    try {
      const response = await enhancedApi.put("v1/payroll/settings", settingsData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour des paramètres paie:", error);
      throw error;
    }
  },

  /**
   * Get employee payroll settings
   */
  async getEmployeePayrollSettings(employeeId) {
    try {
      const response = await enhancedApi.get(`v1/payroll/employees/${employeeId}/settings`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres employé:", error);
      throw error;
    }
  },

  /**
   * Update employee payroll settings
   */
  async updateEmployeePayrollSettings(employeeId, settingsData) {
    try {
      const response = await enhancedApi.put(`v1/payroll/employees/${employeeId}/settings`, settingsData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour des paramètres employé:", error);
      throw error;
    }
  },

  /**
   * Get my payroll slips (employee self-service)
   */
  async getMyPayrollSlips(filters = {}) {
    try {
      const params = { ...filters };
      const response = await enhancedApi.get("v1/payroll/my-slips", { params });
      return response.data || { slips: [], total: 0 };
    } catch (error) {
      console.error("Erreur lors de la récupération de mes bulletins:", error);
      return { slips: [], total: 0 };
    }
  },

  // 🆕 ADDITIONAL METHODS FOR ENHANCED UI

  /**
   * Approve individual payroll entry (via update)
   */
  async approvePayrollEntry(entryId) {
    try {
      const response = await enhancedApi.put(`v1/payroll/entries/${entryId}`, {
        approval_status: "APPROVED"
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'approbation du bulletin:", error);
      throw error;
    }
  },

  /**
   * Reject individual payroll entry (via update)
   */
  async rejectPayrollEntry(entryId, reason = "") {
    try {
      const response = await enhancedApi.put(`v1/payroll/entries/${entryId}`, {
        approval_status: "REJECTED",
        rejection_reason: reason
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du rejet du bulletin:", error);
      throw error;
    }
  },

  /**
   * Export payroll entries as PDF
   */
  async exportPayslips(periodId, entryIds = []) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(
        `v1/payroll/periods/${periodId}/export?format=csv`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        }
      );
      return new Blob([response.data], { type: 'text/csv' });
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error);
      throw error;
    }
  },

  /**
   * Export payroll data as Excel
   */
  async exportPayrollExcel(periodId, entryIds = []) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(
        `v1/payroll/periods/${periodId}/export?format=excel`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        }
      );
      return new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      throw error;
    }
  },

  /**
   * Export payroll summary report
   */
  async exportPayrollSummary(periodId) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(`v1/payroll/periods/${periodId}/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Return the JSON data instead of creating a blob - the backend returns structured data
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'export du rapport:", error);
      throw error;
    }
  },

  /**
   * Export payslips for printing (optimized layout)
   */
  async exportPayslipsPrint(periodId, entryIds = []) {
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const response = await http.get(
        `v1/payroll/periods/${periodId}/export?format=csv`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        }
      );
      // Return CSV for print - browser can handle CSV printing
      return new Blob([response.data], { type: 'text/csv' });
    } catch (error) {
      console.error("Erreur lors de la préparation d'impression:", error);
      throw error;
    }
  },
};

// =====================================
// SERVICE D'AUTHENTIFICATION JWT ENHANCED
// =====================================

export class AuthServiceEnhanced {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.tokenKey = "access_token";
    this.userKey = "user_data";
  }

  async login(username, password, remember_me = false) {
    try {
      const { data } = await enhancedApi.post("v1/auth/login", {
        username,
        password,
        remember_me,
      });

      // Stocker le token et les données utilisateur
      if (remember_me) {
        localStorage.setItem(this.tokenKey, data.access_token);
        localStorage.setItem(this.userKey, JSON.stringify(data.user));
      } else {
        sessionStorage.setItem(this.tokenKey, data.access_token);
        sessionStorage.setItem(this.userKey, JSON.stringify(data.user));
      }

      // Mettre à jour l'intercepteur avec le nouveau token
      this.setAuthHeader(data.access_token);

      // Apply language: use user preference if set, then respect any stored user choice,
      // otherwise default English for Gulf tenants (first-time login only)
      const applyLoginLanguage = () => {
        const preferred = data.user?.preferred_language;
        if (preferred) {
          i18n.changeLanguage(preferred);
          return;
        }
        const storedLang = localStorage.getItem('i18nextLng')?.split('-')[0];
        // Only apply Gulf default when user has never chosen a language (still on 'fr' system default)
        if (!storedLang || storedLang === 'fr') {
          try {
            const companyInfo = JSON.parse(localStorage.getItem('company_info') || '{}');
            const cc = companyInfo?.company?.country_code;
            if (cc === 'QA' || cc === 'SA') {
              i18n.changeLanguage('en');
            }
          } catch (_) {}
        }
      };
      applyLoginLanguage();

      // Toast de succès
      toast.success(i18n.t('auth.loginSuccess'), {
        description: i18n.t('auth.welcome', { name: data.user.full_name }),
        duration: 3000,
      });

      return data;
    } catch (error) {
      // L'erreur est déjà gérée par l'intercepteur enhanced
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const token = this.getToken();
      if (!token) return null;

      const response = await enhancedApi.get("v1/auth/me");
      return response.data;
    } catch (error) {
      // Si l'utilisateur n'est pas authentifié, nettoyer le stockage
      if (error.status === 401) {
        this.logout();
      }
      throw error;
    }
  }

  getToken() {
    return localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
  }

  getUser() {
    const userData = localStorage.getItem(this.userKey) || sessionStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  setAuthHeader(token) {
    if (token) {
      enhancedApi.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete enhancedApi.api.defaults.headers.common["Authorization"];
    }
  }

  logout() {
    const token = this.getToken();
    if (token) {
      enhancedApi.post("v1/auth/logout", {}, { maxRetries: 0 }).catch(() => {});
    }

    // Nettoyer le stockage
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userKey);

    // Retirer l'en-tête d'autorisation
    this.setAuthHeader(null);

    // Toast d'information
    toast.info(i18n.t("apiToasts.logoutTitle"), {
      description: i18n.t("apiToasts.logoutDesc"),
      duration: 2000,
    });

    // Rediriger vers la page de connexion
    window.location.href = "/login";
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Vérifier si le token n'est pas expiré (basique)
      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Date.now() / 1000;
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  hasRole(role) {
    const user = this.getUser();
    return user?.roles?.includes(role) || false;
  }

  hasPermission(permission) {
    const user = this.getUser();
    return user?.permissions?.includes(permission) || user?.permissions?.includes("*:*") || false;
  }

  // Initialiser l'authentification au chargement
  init() {
    const token = this.getToken();
    if (token) {
      this.setAuthHeader(token);
    }
  }
}

// Instance globale du service d'authentification enhanced
export const authServiceEnhanced = new AuthServiceEnhanced();

// Initialiser au chargement du module
authServiceEnhanced.init();

export const securityAPI = {
  async getSummary() {
    const response = await enhancedApi.get("v1/security/summary");
    return response.data;
  },

  async getAuditLogs(params = {}) {
    const response = await enhancedApi.get("v1/security/audit-logs", { params });
    return response.data;
  },

  async getAuditLogsPaginated(params = {}) {
    const response = await enhancedApi.get("v1/security/audit-logs/paginated", { params });
    return response.data;
  },

  async getAuditFacets() {
    const response = await enhancedApi.get("v1/security/audit-logs/facets");
    return response.data;
  },

  async getAuditStats(params = {}) {
    const response = await enhancedApi.get("v1/security/audit-logs/stats", { params });
    return response.data;
  },

  async getLoginHistory(params = {}) {
    const response = await enhancedApi.get("v1/security/login-history", { params });
    return response.data;
  },

  async getEvents(params = {}) {
    const response = await enhancedApi.get("v1/security/events", { params });
    return response.data;
  },

  async acknowledgeEvent(eventId) {
    const response = await enhancedApi.patch(`v1/security/events/${eventId}/acknowledge`);
    return response.data;
  },

  async resolveEvent(eventId) {
    const response = await enhancedApi.patch(`v1/security/events/${eventId}/resolve`);
    return response.data;
  },
};

// Export enhanced API instance and utilities
export default enhancedApi;

// Error handling utilities for components
export const ApiErrorUtils = {
  /**
   * Handle API error in component
   */
  handleComponentError: (error, context = {}) => {
    const enhancedError = globalErrorHandler.handleError(error, {
      ...context,
      component: true,
    });

    showErrorToast(enhancedError);
    return enhancedError;
  },

  /**
   * Create error handler for async operations
   */
  createErrorHandler: (operation, showToast = true) => {
    return (error) => {
      const enhancedError = globalErrorHandler.handleError(error, {
        operation,
        component: true,
      });

      if (showToast) {
        showErrorToast(enhancedError);
      }

      return enhancedError;
    };
  },

  /**
   * Wrap async function with error handling
   */
  withErrorHandling: (fn, operation = "async_operation") => {
    return async (...args) => {
      try {
        return await fn(...args);
      } catch (error) {
        const enhancedError = ApiErrorUtils.handleComponentError(error, {
          operation,
          args: args.length > 0 ? args : undefined,
        });
        throw enhancedError;
      }
    };
  },
};

// ===== RECRUITMENT (ATS) API =====
export const recruitmentAPI = {
  // Job postings
  listPostings: (params) => apiClient.get('v1/recruitment/job-postings/', { params }),
  getPosting: (id) => apiClient.get(`v1/recruitment/job-postings/${id}/`),
  createPosting: (data) => apiClient.post('v1/recruitment/job-postings/', data),
  updatePosting: (id, data) => apiClient.put(`v1/recruitment/job-postings/${id}/`, data),
  publishPosting: (id) => apiClient.post(`v1/recruitment/job-postings/${id}/publish/`),
  closePosting: (id) => apiClient.post(`v1/recruitment/job-postings/${id}/close/`),
  deletePosting: (id) => apiClient.delete(`v1/recruitment/job-postings/${id}/`),

  // Candidates (admin)
  createCandidate: (data) => apiClient.post('v1/recruitment/candidates/', data),

  // Applications
  createApplication: (data) => apiClient.post('v1/recruitment/applications/', data),
  listApplications: (params) => apiClient.get('v1/recruitment/applications/', { params }),
  listApplicationsForPosting: (postingId, params) => apiClient.get(`v1/recruitment/job-postings/${postingId}/applications/`, { params }),
  moveStage: (appId, stage) => apiClient.post(`v1/recruitment/applications/${appId}/move-stage/`, { stage }),
  rejectApplication: (appId, reason) => apiClient.post(`v1/recruitment/applications/${appId}/reject/`, { rejection_reason: reason }),
  hireApplication: (appId) => apiClient.post(`v1/recruitment/applications/${appId}/hire/`),

  // Interviews
  listInterviews: (params) => apiClient.get('v1/recruitment/interviews/', { params }),
  createInterview: (data) => apiClient.post('v1/recruitment/interviews/', data),
  updateInterview: (id, data) => apiClient.put(`v1/recruitment/interviews/${id}/`, data),
  evaluateInterview: (id, data) => apiClient.post(`v1/recruitment/interviews/${id}/evaluate/`, data),

  // Analytics
  getStats: () => apiClient.get('v1/recruitment/stats/'),
  convertToEmployee: (appId, payload = {}) => apiClient.post(`v1/recruitment/applications/${appId}/convert-to-employee/`, payload),
  listDepartments: () => apiClient.get('v1/organization-chart/'),
  listSites: () => apiClient.get('v1/sites/'),

  // External publish (LinkedIn / Indeed)
  getExternalStatus: () => apiClient.get('v1/recruitment/external-publish/status/'),
  publishToLinkedIn: (id) => apiClient.post(`v1/recruitment/job-postings/${id}/publish-linkedin/`),
  publishToIndeed: (id) => apiClient.post(`v1/recruitment/job-postings/${id}/publish-indeed/`),
  unpublishExternal: (id, platform) => apiClient.post(`v1/recruitment/job-postings/${id}/unpublish-external/?platform=${platform}`),

  // Notes
  listNotes: (appId) => apiClient.get(`v1/recruitment/applications/${appId}/notes/`),
  addNote: (appId, content) => apiClient.post(`v1/recruitment/applications/${appId}/notes/`, { content }),
  deleteNote: (appId, noteId) => apiClient.delete(`v1/recruitment/applications/${appId}/notes/${noteId}/`),

  // Stage history
  listStageHistory: (appId) => apiClient.get(`v1/recruitment/applications/${appId}/stage-history/`),

  // Questions
  listQuestions: (postingId) => apiClient.get(`v1/recruitment/job-postings/${postingId}/questions/`),
  createQuestion: (postingId, data) => apiClient.post(`v1/recruitment/job-postings/${postingId}/questions/`, data),
  updateQuestion: (postingId, qId, data) => apiClient.put(`v1/recruitment/job-postings/${postingId}/questions/${qId}/`, data),
  deleteQuestion: (postingId, qId) => apiClient.delete(`v1/recruitment/job-postings/${postingId}/questions/${qId}/`),

  // Export
  exportCSV: (params = {}) => apiClient.get('v1/recruitment/applications/export.csv', { params, responseType: 'blob' }),
};

// ===== CAREERS PORTAL (public, no auth) =====
export const careersAPI = {
  getInfo: () => apiClient.get('v1/careers/info/'),
  listPostings: (params) => apiClient.get('v1/careers/postings/', { params }),
  getPosting: (id) => apiClient.get(`v1/careers/postings/${id}/`),
  apply: (data) => apiClient.post('v1/careers/apply/', data),
  trackApplication: (token) => apiClient.get(`v1/careers/my-application/${token}/`),
  uploadCV: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post('v1/careers/upload-cv/', fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  downloadCV: (fileId) => apiClient.get(`v1/recruitment/cv/${fileId}`, { responseType: "blob" }),
};

// ===== DAILY ATTENDANCE CALCULATION API =====
export const dailyAttendanceAPI = {
  /** Trigger calculation and persist results */
  calculate(payload) {
    return apiClient.post('v1/daily-attendance/calculate', payload);
  },

  /** Fetch persisted results */
  getResults(params) {
    return apiClient.get('v1/daily-attendance/', { params });
  },

  /** Preview employee list before calculation */
  getPreview(params) {
    return apiClient.get('v1/daily-attendance/preview', { params });
  },
};

